import { z } from 'zod';
import { DFMCheckSchema } from './dfm-check.model';
import { PullDirectionSchema } from './pull-direction.model';

export const MouldingStepParameterValue = z.union([z.number(), z.boolean()]);
export type MouldingStepParameterValue = z.infer<
  typeof MouldingStepParameterValue
>;
export const MouldingStepParameterPriority = z.enum(['high', 'medium', 'low']);
export type MouldingStepParameterPriority = z.infer<
  typeof MouldingStepParameterPriority
>;
export const MouldingStepParameters = z.record(
  z.string(),
  MouldingStepParameterValue,
);
export type MouldingStepParameters = z.infer<typeof MouldingStepParameters>;

export const CadArtifactRef = z.object({
  cad_file_id: z.number().int().positive(),
  primary_cad_object_id: z.number().int().positive(),
  pull_direction: PullDirectionSchema.optional(),
});
export type CadArtifactRef = z.infer<typeof CadArtifactRef>;

export const GeneratedFileRef = z.object({
  kind: z.string(),
  url: z.string(),
});
export type GeneratedFileRef = z.infer<typeof GeneratedFileRef>;

export const MouldingNodeRunResult = z.object({
  artifact: CadArtifactRef,
  pull_directions: z.array(PullDirectionSchema).default([]),
  generated_files: z.array(GeneratedFileRef).default([]),
  dfm_checks: z.array(DFMCheckSchema).optional(),
});
export type MouldingNodeRunResult = z.infer<typeof MouldingNodeRunResult>;

export const MouldingNodeKind = z.enum([
  'cad_input',
  'pull_direction',
  'dfm_check',
  'split_part',
  'fill_holes_and_cutouts',
  'add_runoff',
  'scale_cad',
  'create_mold_box',
  'create_pattern',
  'drill_lt_holes',
  'add_structure',
]);
export type MouldingNodeKind = z.infer<typeof MouldingNodeKind>;

export const MouldingGraphNode = z.object({
  id: z.string(),
  kind: MouldingNodeKind,
  name: z.string().trim().min(1).max(255).optional(),
  parameters: MouldingStepParameters.optional(),
  position: z.object({ x: z.number(), y: z.number() }),
});
export type MouldingGraphNodeData = z.infer<typeof MouldingGraphNode>;

export const MouldingGraphConnection = z.object({
  source: z.string(),
  sourceOutput: z.string(),
  target: z.string(),
  targetInput: z.string(),
});

export const MouldingGraphViewport = z.object({
  x: z.number(),
  y: z.number(),
  k: z.number().min(0.1).max(5),
});

export const MouldingGraph = z.object({
  nodes: z.array(MouldingGraphNode).default([]),
  connections: z.array(MouldingGraphConnection).default([]),
  viewport: MouldingGraphViewport.optional().catch(undefined),
});
export type MouldingGraph = z.infer<typeof MouldingGraph>;

/** Persisted execution status for a node. */
export const MouldingNodeRunStatus = z.enum([
  'pending',
  'processing',
  'complete',
  'error',
]);
export type MouldingNodeRunStatus = z.infer<typeof MouldingNodeRunStatus>;

/**
 * Tracks persisted execution state for a single node in a flow. Delivered both via the flow
 * detail endpoint (`MouldingItem.node_runs`, for reconnecting clients) and
 * live over the flow's WebSocket connection (handled by MouldingPageComponent).
 */
export const MouldingNodeRun = z.object({
  id: z.number(),
  node_id: z.string(),
  step_type: z.string(),
  status: MouldingNodeRunStatus,
  error_message: z.string(),
  is_outdated: z.boolean().default(false),
  parameters: MouldingStepParameters.default({}),
  pull_direction: PullDirectionSchema.nullable(),
  pull_directions: z.array(PullDirectionSchema).default([]),
  cad_file_id: z.number().nullable(),
  primary_cad_object_id: z.number().nullable(),
  generated_files: z.array(GeneratedFileRef).default([]),
  dfm_checks: z.array(DFMCheckSchema).default([]),
  created_date: z.iso.datetime(),
  updated_date: z.iso.datetime(),
});
export type MouldingNodeRun = z.infer<typeof MouldingNodeRun>;

export const MouldingDownstreamExecution = z.object({
  status: z.enum(['started', 'completed', 'failed']),
  error_message: z.string().nullable(),
});
export type MouldingDownstreamExecution = z.infer<
  typeof MouldingDownstreamExecution
>;

export const MouldingFlowSummary = z.object({
  id: z.number(),
  name: z.string(),
  creator_email: z.string().nullable(),
});
export type MouldingFlowSummary = z.infer<typeof MouldingFlowSummary>;

export const MouldingStepDefinitions = z.object({
  step_parameter_defaults: z
    .record(z.string(), MouldingStepParameters)
    .default({}),
  step_parameter_descriptions: z
    .record(z.string(), z.record(z.string(), z.string()))
    .default({}),
  step_parameter_priorities: z.record(
    z.string(),
    z.record(z.string(), MouldingStepParameterPriority),
  ),
  step_parameter_units: z
    .record(z.string(), z.record(z.string(), z.string().nullable()))
    .default({}),
  step_descriptions: z.record(z.string(), z.string()).default({}),
});
export type MouldingStepDefinitions = z.infer<typeof MouldingStepDefinitions>;

export const MouldingItem = z.object({
  id: z.number(),
  name: z.string(),
  graph: MouldingGraph.optional(),
  run_downstream_enabled: z.boolean().default(true),
  node_runs: z.array(MouldingNodeRun).default([]),
});

export type MouldingItem = z.infer<typeof MouldingItem>;
